import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PILLARS, IMAGES } from "../data/content";
import { PageHero, Reveal, Eyebrow, useSeo } from "../components/Reveal";
import { UrgentButton } from "../components/UrgentModal";

export default function About() {
  useSeo(
    "About SwiftTest Solutions LLC | Mobile Testing Built Around You",
    "SwiftTest Solutions LLC was founded to eliminate the travel time and delays of traditional testing centers. Certified, confidential, mobile-first testing across Eastern Iowa."
  );

  return (
    <div data-testid="about-page">
      <PageHero
        testId="about-hero"
        eyebrow="About SwiftTest"
        title="Testing that revolves around your day — not ours"
        lead="SwiftTest Solutions LLC was founded on a simple frustration: traditional testing centers waste your time. So we removed the center entirely."
      />

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-3 rotate-2 rounded-[2.2rem] border-2 border-teal-200" aria-hidden="true" />
                <img
                  data-testid="about-team-image"
                  src={IMAGES.team.url}
                  alt={IMAGES.team.alt}
                  className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-2xl"
                />
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <Eyebrow>Our story</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Built to end the clinic run-around
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
                <p>
                  Every employer in Eastern Iowa knows the drill: send an employee across town to a
                  testing center, lose half a shift, and wait days for paperwork. Individuals face
                  the same hassle — limited hours, crowded waiting rooms, zero privacy.
                </p>
                <p>
                  SwiftTest Solutions LLC was founded to eliminate that hassle, travel time, and
                  operational delay. Our certified collectors bring clinic-grade testing directly to
                  offices, job sites, facilities, and homes — on your schedule, including last-minute
                  and after-hours calls.
                </p>
                <p>
                  From single DNA collections to company-wide random testing programs, every visit
                  is handled with the same clinical rigor, documented chain-of-custody, and genuine
                  respect for the people we serve.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal className="max-w-2xl">
            <Eyebrow>What we stand for</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Four pillars behind every collection
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07}>
                <div
                  data-testid={`about-pillar-${i}`}
                  className="flex h-full gap-5 rounded-3xl border border-slate-200 bg-background p-7"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-teal-700 text-white">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-900">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <div className="flex flex-col items-center justify-between gap-6 rounded-[2.5rem] bg-slate-900 p-8 text-center sm:flex-row sm:p-12 sm:text-left">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Ready to skip the waiting room?
                </h3>
                <p className="mt-2 max-w-lg text-sm text-slate-300 sm:text-base">
                  Book a planned visit or reach us any time for urgent dispatch.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <UrgentButton testId="about-need-testing-now-button" />
                <Link
                  data-testid="about-contact-link"
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-slate-900 transition-all hover:-translate-y-0.5 hover:bg-teal-100"
                >
                  Request Testing <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
